import React, { useState, useEffect, useMemo } from "react";
import { Alert, IconButton } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { red, orange, blue, green, grey } from "@mui/material/colors";

/**
 * DismissibleAlert - Reusable alert component with localStorage persistence
 *
 * @param {Object} props
 * @param {string} alertId - Unique ID for localStorage (e.g., "underwriting-alert")
 * @param {ReactNode} children - Alert content/text
 * @param {ReactNode} [icon] - Custom icon element
 * @param {string} [severity] - MUI Alert severity: "error", "warning", "info", "success"
 * @param {string} [color] - Custom color override (e.g., red[100])
 * @param {string} [fontSize] - Font size (default: "14px")
 * @param {number} [hideDuration] - Hours to hide after dismiss (default: 24)
 * @param {Object} [sx] - Additional MUI sx styles
 * @param {boolean} [show] - External control for showing/hiding
 * @param {Function} [onDismiss] - Callback when dismissed
 */
const DismissibleAlert = ({
  alertId,
  children,
  icon,
  severity = "error",
  color,
  fontSize = "12px",
  action,
  onClose,
  hideDuration = 24, // hours
  sx = {},
  show = true,
  onDismiss,
}) => {
  const [isDismissed, setIsDismissed] = useState(false);

  // Check localStorage on mount
  useEffect(() => {
    if (!alertId) return;

    const dismissedData = localStorage.getItem(`alert-dismissed-${alertId}`);
    if (dismissedData) {
      const { timestamp } = JSON.parse(dismissedData);
      const hoursSinceDismiss = (Date.now() - timestamp) / (1000 * 60 * 60);

      if (hoursSinceDismiss < hideDuration) {
        setIsDismissed(true);
      } else {
        // Expired, remove from localStorage
        localStorage.removeItem(`alert-dismissed-${alertId}`);
      }
    }
  }, [alertId, hideDuration]);

  // Get border color based on severity
  const borderColor = useMemo(() => {
    if (color) return color;

    const severityColors = {
      error: red[100],
      warning: orange[100],
      info: blue[100],
      success: green[100],
    };

    return severityColors[severity] || grey[300];
  }, [severity, color]);

  const handleDismiss = () => {
    if (alertId) {
      localStorage.setItem(
        `alert-dismissed-${alertId}`,
        JSON.stringify({
          timestamp: Date.now(),
        }),
      );
    }

    setIsDismissed(true);

    if (onDismiss) {
      onDismiss();
    }
  };

  // Don't render if dismissed or externally hidden
  if (isDismissed || !show) {
    return null;
  }

  return (
    <Alert
      severity={severity}
      icon={icon}
      sx={{
        border: `1px solid ${borderColor}`,
        fontSize: fontSize,
        ...sx,
      }}
      action={
        action ? (
          action
        ) : (
          <IconButton
            aria-label="close"
            color="inherit"
            size="small"
            onClick={handleDismiss}
          >
            <CloseIcon fontSize="inherit" />
          </IconButton>
        )
      }
    >
      {children}
    </Alert>
  );
};

export default DismissibleAlert;
